import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import SignatureCanvasComponent from '../components/common/SignatureCanvasComponent';
import DeliverySelector from '../components/common/DeliverySelector';
import MobileStickyBar from '../components/common/MobileStickyBar';
import CallButton from '../components/common/CallButton';
import { useAuth } from '../context/AuthContext';
import { authHeaders } from '../lib/authToken';
import { toast } from 'sonner';
import { PROFILE_DISCOUNTS } from '../config/discounts';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import {
  Shield, Check, Download, PenLine, ArrowRight,
  AlertCircle, History, RefreshCw, Send, FileText, Star,
  Wallet, Tag
} from 'lucide-react';
import { formatDate, formatDateTime } from '../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  brouillon: { label: 'Brouillon', color: '#CFCFCF', bg: 'rgba(207,207,207,0.1)' },
  en_attente: { label: 'En attente', color: '#7DB1FF', bg: 'rgba(125,177,255,0.1)' },
  envoye: { label: 'Envoyé', color: '#7DB1FF', bg: 'rgba(125,177,255,0.1)' },
  signe: { label: 'Signé ✓', color: '#3DDC97', bg: 'rgba(61,220,151,0.1)' },
  acompte_paye: { label: 'Acompte payé', color: 'var(--teal)', bg: 'rgba(212,175,55,0.1)' },
  solde_paye: { label: 'Soldé ✓', color: '#3DDC97', bg: 'rgba(61,220,151,0.1)' },
  annule: { label: 'Annulé', color: '#FF5C6C', bg: 'rgba(255,92,108,0.1)' },
};

const EVENT_LABELS = {
  anniversaire_enfant: 'Anniversaire enfant',
  anniversaire_adulte: 'Anniversaire adulte',
  mariage: 'Mariage',
  bapteme: 'Baptême',
  communion: 'Communion',
  fete_privee: 'Fête privée',
  entreprise: 'Entreprise',
  animation_commerciale: 'Animation commerciale',
};

export default function DevisPage() {
  const company = useCompanyInfo();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [quote, setQuote] = useState(null);
  const [bonAvoirDispo, setBonAvoirDispo] = useState(0);
  const [applyingBA, setApplyingBA] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSignCanvas, setShowSignCanvas] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState({ tarif_total: 0, option_label: 'Livraison + Reprise' });
  const [showDelivery, setShowDelivery] = useState(false);
  const [signing, setSigning] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [soldeLoading, setSoldeLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  // Wallet parrainage
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletApplied, setWalletApplied] = useState(false);
  const [walletAmount, setWalletAmount] = useState(0);

  // Charger wallet parrainage si utilisateur connecté
  useEffect(() => {
    if (user?.id) {
      fetch(`${BACKEND}/api/parrainage/wallet/${user.id}`)
        .then(r => r.json())
        .then(d => { if (d.balance > 0) { setWalletBalance(d.balance); setWalletAmount(Math.min(d.balance, quote?.acompte || 0)); } })
        .catch((err) => { /* eslint-disable-line no-empty */ });
    }
  // eslint-disable-next-line
  }, [user, quote]);

  // Check payment status from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (id) fetchQuote();
    else setLoading(false);
  // eslint-disable-next-line
  }, [id]);

  async function fetchQuote() {
    try {
      const sigToken = new URLSearchParams(window.location.search).get('t') || '';
      const tok = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth') || '';
      const qs = sigToken ? `?t=${encodeURIComponent(sigToken)}` : '';
      const res = await fetch(`${BACKEND}/api/devis/${id}${qs}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setQuote(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  // Bon d'avoir disponible pour ce client (si connecté + propriétaire)
  useEffect(() => {
    if (token && quote?.id) {
      fetch(`${BACKEND}/api/devis/${quote.id}/bon-avoir-dispo`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setBonAvoirDispo(d.applicable || 0); })
        .catch(() => {});
    }
  // eslint-disable-next-line
  }, [token, quote?.id, quote?.total]);

  async function applyBonAvoir() {
    if (!token) { toast.error('Connectez-vous pour utiliser votre bon d\'avoir'); return; }
    setApplyingBA(true);
    try {
      const res = await fetch(`${BACKEND}/api/devis/${id}/appliquer-bon-avoir`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Erreur');
      toast.success('Bon d\'avoir appliqué ✓', { description: `-${(d.montant_applique || 0).toFixed(2)}€ · nouveau total ${(d.total || 0).toFixed(2)}€` });
      setBonAvoirDispo(0);
      await fetchQuote();
    } catch (e) { toast.error(e.message || 'Erreur'); }
    setApplyingBA(false);
  }

  async function fetchHistory() {
    try {
      const res = await fetch(`${BACKEND}/api/devis/${id}/historique`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
        setShowHistory(true);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('[DevisPage] fetchHistory:', e);
    }
  }

  async function pollPaymentStatus(sessionId, attempts = 0) {
    if (attempts >= 5) return;
    try {
      const res = await fetch(`${BACKEND}/api/paiement/status/${sessionId}`);
      const data = await res.json();
      if (data.payment_status === 'paid') {
        toast.success('Paiement confirmé !', { description: 'Votre paiement a été encaissé avec succès.' });
        if (id) fetchQuote();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('[DevisPage] pollPaymentStatus:', e);
    }
  }

  async function handleSign(signaturePayload) {
    setSigning(true);
    try {
      const sigToken = new URLSearchParams(window.location.search).get('t') || '';
      const res = await fetch(`${BACKEND}/api/devis/${id}/signer`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ ...signaturePayload, t: sigToken })
      });
      if (res.ok) {
        const updated = await res.json();
        setQuote(updated);
        setShowSignCanvas(false);

        // Créer le compte si demandé
        if (signaturePayload.create_account && signaturePayload.password && quote?.client_email) {
          try {
            const regRes = await fetch(`${BACKEND}/api/auth/register`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: quote.client_email,
                password: signaturePayload.password,
                nom: (signaturePayload.nom || '').split(' ').slice(1).join(' ') || signaturePayload.nom || '',
                prenom: (signaturePayload.nom || '').split(' ')[0] || '',
                adresse_personnelle: signaturePayload.adresse || '',
              })
            });
            if (regRes.ok) {
              toast.success('Compte créé ! Vous pouvez vous connecter sur votre espace client.');
            }
          } catch { /* non bloquant */ }
        }

        toast.success('Contrat signé !', {
          description: `Signature enregistrée le ${formatDate(new Date())} — valeur légale`
        });
      } else {
        toast.error('Erreur lors de la signature');
      }
    } catch (e) {
      toast.error('Erreur de connexion');
    } finally {
      setSigning(false);
    }
  }

  async function initiatePayment(paymentType = 'acompte') {
    const isAcompte = paymentType === 'acompte';
    if (isAcompte) setPaymentLoading(true);
    else setSoldeLoading(true);

    try {
      const res = await fetch(`${BACKEND}/api/paiement/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          quote_id: id,
          payment_type: paymentType,
          origin_url: window.location.origin
        })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (e) {
      toast.error('Erreur paiement');
    } finally {
      if (isAcompte) setPaymentLoading(false);
      else setSoldeLoading(false);
    }
  }

  async function downloadPDF() {
    try {
      const sigToken = new URLSearchParams(window.location.search).get('t') || '';
      const tok = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth') || '';
      const qs = sigToken ? `?t=${encodeURIComponent(sigToken)}` : '';
      const res = await fetch(`${BACKEND}/api/devis/${id}/pdf${qs}`, {
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `devis-${quote?.numero || id}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } else {
        toast.error('Lien expiré ou accès refusé. Ouvrez le devis depuis le lien reçu par e-mail, ou connectez-vous.');
      }
    } catch (e) {
      toast.error('Erreur téléchargement PDF');
    }
  }

  if (loading) {
    return (
      <div style={{ background: 'var(--bg, #FBF7F1)', minHeight: '100vh' }}>
        <Header />
        <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
          <div className="animate-spin w-10 h-10 rounded-full" style={{ border: '2px solid #2A2A2A', borderTopColor: '#D4AF37' }} />
        </div>
      </div>
    );
  }

  if (!id || !quote) {
    return (
      <div style={{ background: 'var(--bg, #FBF7F1)', minHeight: '100vh' }}>
        <Header />
        <div className="container-ose text-center" style={{ paddingTop: '6rem' }}>
          <AlertCircle size={48} className="mx-auto mb-4" style={{ color: '#FF5C6C' }} />
          <h1 className="font-display text-2xl font-bold mb-4" style={{ color: 'var(--ink)' }}>Devis non trouvé</h1>
          <p className="mb-6" style={{ color: 'var(--text-muted)' }}>Ce devis n'existe pas ou a été supprimé.</p>
          <Link to="/creer-evenement" className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold">
            Créer un devis
          </Link>
        </div>
        <MobileStickyBar />
      <Footer />
      </div>
    );
  }

  const statusInfo = STATUS_LABELS[quote.statut] || STATUS_LABELS.brouillon;
  // §3.2 (ANO-ECC-05) : payer l'acompte → signer UNE fois (devis + contrat de location).
  const acompteRegle = ['acompte_paye', 'solde_paye'].includes(quote.statut) || quote.acompte_paye === true;
  const contratSigne = !!(quote.contrat_signe || quote.signature_contrat_date || quote.signature_data);
  const canPayAcompte = !acompteRegle && ['brouillon', 'en_attente', 'envoye', 'signe'].includes(quote.statut);
  const canSign = acompteRegle && !contratSigne;                 // signer le contrat APRÈS l'acompte
  const canPaySolde = quote.statut === 'acompte_paye' && contratSigne;  // solde après signature du contrat
  const isPaid = ['acompte_paye', 'solde_paye'].includes(quote.statut);

  // Profil pro / asso → paiement à la livraison autorisé
  const isProOrAsso = ['entreprise', 'association', 'ce', 'groupe'].includes(quote.client_profile);

  // Calcul jours restants avant événement
  const dateEvenement = quote.session?.date_evenement;
  const joursAvantEvenement = dateEvenement
    ? Math.ceil((new Date(dateEvenement + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  // Si moins de 7 jours → paiement total uniquement en CB
  const urgentLessDe7Jours = joursAvantEvenement !== null && joursAvantEvenement < 7;

  return (
    <div style={{ background: 'var(--bg, #FBF7F1)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '4rem', maxWidth: '800px' }}>
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)' }}>
                Devis {quote.numero}
              </h1>
              {quote.version > 1 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-mono-ose" style={{ background: 'rgba(197,216,249,0.15)', color: '#C5D8F9' }}>
                  V{quote.version}
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: 'rgba(26,26,46,0.4)' }}>
              Créé le {formatDate(quote.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* PDF Download */}
            <button
              onClick={downloadPDF}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
              style={{ background: 'rgba(197,216,249,0.1)', color: '#C5D8F9', border: '1px solid rgba(197,216,249,0.2)' }}
              data-testid="download-pdf-button"
            >
              <Download size={14} /> PDF
            </button>
            {/* History */}
            {quote.versions_history?.length > 0 && (
              <button
                onClick={fetchHistory}
                data-testid="devis-historique-btn"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
                style={{ background: 'rgba(255,204,102,0.1)', color: '#FFCC66', border: '1px solid rgba(255,204,102,0.2)' }}
              >
                <History size={14} /> Historique V{quote.version}
              </button>
            )}
            <span
              data-testid="quote-status-badge"
              className="px-3 py-1.5 rounded-full text-sm font-semibold"
              style={{ background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.color}30` }}
            >
              {statusInfo.label}
            </span>
          </div>
        </div>

        {/* History modal */}
        {showHistory && history.length > 0 && (
          <div className="mb-4 p-4 rounded-xl" style={{ background: 'rgba(255,204,102,0.06)', border: '1px solid rgba(255,204,102,0.2)' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm" style={{ color: '#FFCC66' }}>Historique des versions</h3>
              <button onClick={() => setShowHistory(false)} style={{ color: 'rgba(26,26,46,0.4)' }}>✕</button>
            </div>
            {history.map((v, i) => (
              <div key={`col-${i}`} className="text-xs py-2" style={{ borderBottom: '1px solid rgba(255,204,102,0.1)', color: 'rgba(255,255,255,0.6)' }}>
                <span style={{ color: '#FFCC66' }}>V{v.version}</span> — {formatDate(v.snapshot_at)} — {v.reason} — Total: {v.total?.toFixed(2)}€
              </div>
            ))}
          </div>
        )}

        {/* Event details */}
        <div className="p-5 rounded-xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--teal)' }}>Détails de l'événement</h2>
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div><span style={{ color: 'rgba(26,26,46,0.4)' }}>Type :</span> <span style={{ color: 'var(--ink)' }}>{EVENT_LABELS[quote.session?.type_evenement] || quote.session?.type_evenement}</span></div>
            {quote.session?.date_evenement && <div><span style={{ color: 'rgba(26,26,46,0.4)' }}>Date :</span> <span style={{ color: 'var(--ink)' }}>{formatDate(quote.session.date_evenement)}</span></div>}
            {quote.session?.adresse && <div className="sm:col-span-2"><span style={{ color: 'rgba(26,26,46,0.4)' }}>Lieu :</span> <span style={{ color: 'var(--ink)' }}>{quote.session.adresse}</span></div>}
            <div><span style={{ color: 'rgba(26,26,46,0.4)' }}>Personnes :</span> <span style={{ color: 'var(--ink)' }}>{(quote.session?.nb_adultes || 0) + (quote.session?.nb_enfants || 0)} participants</span></div>
            <div><span style={{ color: 'rgba(26,26,46,0.4)' }}>Profil :</span> <span style={{ color: 'var(--ink)' }} className="capitalize">{quote.client_profile}</span></div>
          </div>
        </div>

        {/* Items */}
        <div className="p-5 rounded-xl mb-4" data-testid="quote-page" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
          <h2 className="font-semibold text-sm mb-4" style={{ color: 'var(--teal)' }}>Prestations</h2>

          {/* Badge remise — valeur canonique du backend (aucun recalcul front, ANO-ECC-02) */}
          {(() => {
            const taux = Math.max(0, ...(quote.items || []).map(it => Number(it.taux_remise || 0)));
            if (!(taux > 0)) return null;
            const pct = Math.round(taux * 100);
            const pCfg = PROFILE_DISCOUNTS[quote.client_profile] || PROFILE_DISCOUNTS.particulier;
            const libelle = quote.client_profile === 'particulier'
              ? `Tarif durée : -${pct}% appliqué sur les locations`
              : `Tarif ${pCfg.label} : -${pct}% appliqué sur toutes les prestations`;
            return (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-4"
                style={{ background: pCfg.bg || 'rgba(31,157,122,0.08)', border: `1px solid ${pCfg.color}30` }}>
                <Tag size={13} style={{ color: pCfg.color }} />
                <div>
                  <p className="text-xs font-bold" style={{ color: pCfg.color }}>{libelle}</p>
                </div>
              </div>
            );
          })()}

          <div className="space-y-3">
            {quote.items?.map((item, i) => {
              const eco = Number(item.economie || 0);
              const taux = Number(item.taux_remise || 0);
              const hasDiscount = eco > 0;
              const qte = Number(item.quantite || 1);
              const publicTotal = item.prix_public != null
                ? Number(item.prix_public) * qte
                : Number(item.sous_total || 0) + eco;
              const pCfg = PROFILE_DISCOUNTS[quote.client_profile] || PROFILE_DISCOUNTS.particulier;
              return (
                <div key={`list-${i}`} className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-medium text-sm" style={{ color: 'var(--ink)' }}>{item.product_nom}</div>
                    <div className="text-xs flex items-center gap-1.5 flex-wrap mt-0.5" style={{ color: 'rgba(26,26,46,0.4)' }}>
                      <span>{item.product_type} × {item.quantite}</span>
                      {hasDiscount && (
                        <span className="px-1.5 py-0.5 rounded-full font-semibold"
                          style={{ background: pCfg.bg || 'rgba(31,157,122,0.08)', color: pCfg.color, fontSize: '10px' }}>
                          -{Math.round(taux * 100)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    {hasDiscount && (
                      <div className="text-xs line-through mb-0.5" style={{ color: 'rgba(26,26,46,0.3)' }}>
                        {publicTotal.toFixed(2)}€
                      </div>
                    )}
                    <div className="price-gold text-sm">{item.sous_total?.toFixed(2)}€</div>
                    {hasDiscount && (
                      <div className="text-xs font-semibold" style={{ color: pCfg.color }}>
                        -{eco.toFixed(2)}€
                      </div>
                    )}
                    <div className="text-xs" style={{ color: 'rgba(26,26,46,0.4)' }}>{item.prix_unitaire?.toFixed(2)}€ / unité</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="divider-gold mt-4 mb-4" />

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--text-muted)' }}>Sous-total{(quote.economie_totale > 0) ? ' (prix public)' : ''}</span>
              <span style={{ color: 'var(--ink)' }}>{(quote.sous_total_public ?? quote.sous_total)?.toFixed(2)}€</span>
            </div>
            {/* Économie totale — valeur canonique backend (aucun recalcul front, ANO-ECC-02) */}
            {(() => {
              const totalSaving = Number(
                quote.economie_totale ??
                ((quote.items || []).reduce((acc, item) => acc + Number(item.economie || 0), 0))
              );
              if (!(totalSaving > 0)) return null;
              const pCfg = PROFILE_DISCOUNTS[quote.client_profile] || PROFILE_DISCOUNTS.particulier;
              return (
                <div className="flex justify-between text-sm items-center py-1 px-2 rounded-lg"
                  style={{ background: pCfg.bg || 'rgba(31,157,122,0.06)' }}>
                  <span className="flex items-center gap-1.5 font-medium" style={{ color: pCfg.color }}>
                    <Tag size={12} /> Vous économisez
                  </span>
                  <span className="font-bold" style={{ color: pCfg.color }}>-{totalSaving.toFixed(2)}€</span>
                </div>
              );
            })()}
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--text-muted)' }}>Acompte (30%)</span>
              <span style={{ color: 'var(--teal)' }}>{quote.acompte?.toFixed(2)}€</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: 'var(--text-muted)' }}>Solde restant</span>
              <span style={{ color: 'var(--text-muted)' }}>{quote.solde?.toFixed(2)}€</span>
            </div>
            {(() => {
              const ba = (quote.items || []).find(it => it.bon_avoir);
              if (!ba) return null;
              return (
                <div className="flex justify-between text-sm items-center py-1 px-2 rounded-lg" style={{ background: 'rgba(96,165,250,0.08)' }}>
                  <span className="flex items-center gap-1.5 font-medium" style={{ color: '#2563EB' }}>
                    <Tag size={12} /> Bon d'avoir appliqué
                  </span>
                  <span className="font-bold" style={{ color: '#2563EB' }}>{Number(ba.prix_unitaire).toFixed(2)}€</span>
                </div>
              );
            })()}
            <div className="flex justify-between font-bold pt-2" style={{ borderTop: '1px solid #2A2A2A' }}>
              <span style={{ color: 'var(--ink)' }}>Total TTC</span>
              <span className="price-gold text-lg">{quote.total?.toFixed(2)}€</span>
            </div>
          </div>
        </div>

        {bonAvoirDispo > 0 && canPayAcompte && (
          <button onClick={applyBonAvoir} disabled={applyingBA} data-testid="apply-bon-avoir-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold mb-4"
            style={{ background: 'rgba(96,165,250,0.12)', color: '#2563EB', border: '1px solid rgba(96,165,250,0.35)' }}>
            <Tag size={15} /> {applyingBA ? 'Application…' : `Utiliser mon bon d'avoir (${bonAvoirDispo.toFixed(2)}€)`}
          </button>
        )}

        {/* Admin notes */}
        {quote.notes_admin && (
          <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(125,177,255,0.08)', border: '1px solid rgba(125,177,255,0.2)' }}>
            <p className="text-sm font-semibold mb-1" style={{ color: '#7DB1FF' }}>
              <Send size={13} className="inline mr-1" /> Note de votre conseiller
            </p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{quote.notes_admin}</p>
          </div>
        )}

        {/* Signature zone — CONTRAT (devis + conditions), APRÈS l'acompte (§3.2 / ANO-ECC-05) */}
        {canSign && !showSignCanvas && (
          <div className="p-5 rounded-xl mb-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
            <h2 className="font-semibold text-sm mb-3" style={{ color: 'var(--teal)' }}>
              <PenLine size={16} className="inline mr-1" /> Dernière étape : signer le contrat de location
            </h2>
            <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
              Votre acompte est encaissé. Signez une seule fois le contrat de location (devis + conditions ci-dessous) pour confirmer votre réservation. La signature est horodatée et a valeur légale.
            </p>
            <button
              data-testid="quote-signature-trigger"
              onClick={() => setShowSignCanvas(true)}
              className="btn-outline-gold px-5 py-2.5 text-sm font-semibold rounded-xl w-full"
            >
              <PenLine size={16} className="inline mr-2" /> Signer le contrat
            </button>
          </div>
        )}

        {/* Signature canvas */}
        {canSign && showSignCanvas && (
          <div className="p-5 rounded-xl mb-4" style={{ background: '#FFFFFF', border: '1px solid rgba(212,175,55,0.3)' }}>
            <h2 className="font-semibold text-sm mb-4" style={{ color: 'var(--teal)' }}>
              <PenLine size={16} className="inline mr-1" /> Signez votre contrat de location
            </h2>
            {/* Récapitulatif avant la signature UNIQUE (devis + contrat de location) — valeur juridique */}
            <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(31,157,122,0.05)', border: '1px solid rgba(31,157,122,0.2)' }} data-testid="signature-recap">
              <p className="font-bold mb-2" style={{ color: 'var(--teal)', fontSize: '12px' }}>Récapitulatif — ce que vous signez</p>
              <div style={{ color: 'var(--ink)', fontSize: '12px' }}>
                <div className="flex justify-between py-0.5"><span>Devis</span><strong>{quote.numero}</strong></div>
                <div className="flex justify-between py-0.5"><span>Total TTC</span><strong>{quote.total?.toFixed(2)}€</strong></div>
                <div className="flex justify-between py-0.5"><span>Acompte (30 %)</span><strong>{quote.acompte?.toFixed(2)}€</strong></div>
                <div className="flex justify-between py-0.5"><span>Solde</span><strong>{quote.solde?.toFixed(2)}€</strong></div>
                {quote.session?.date_livraison && <div className="flex justify-between py-0.5"><span>Livraison</span><strong>{quote.session.date_livraison}</strong></div>}
                {quote.session?.date_reprise && <div className="flex justify-between py-0.5"><span>Reprise</span><strong>{quote.session.date_reprise} {quote.session?.heure_reprise || ''}</strong></div>}
              </div>
              <p className="font-semibold mt-3 mb-1" style={{ color: 'var(--ink)', fontSize: '11px' }}>Conditions de location</p>
              <ul className="list-disc pl-4" style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                <li>Le matériel reste la propriété de {company.raison_sociale} pendant toute la location.</li>
                <li>Retour du matériel le lendemain avant 10h00 (sauf mention contraire).</li>
                <li>Le locataire est responsable du matériel de la livraison à la reprise.</li>
                <li>L'acompte de 30 % bloque la date ; il est conservé en cas d'annulation.</li>
              </ul>
              <p className="mt-3 p-2 rounded-lg" style={{ background: 'rgba(31,157,122,0.1)', color: 'var(--teal)', fontSize: '11px' }} data-testid="signature-legal-note">
                ✍️ Votre signature vaut <strong>acceptation du devis</strong> ci-dessus <strong>et du contrat de location</strong> : un seul document, une seule signature, une trace juridique complète.
              </p>
            </div>
            <SignatureCanvasComponent
              clientNom={quote.client_nom || ''}
              onSign={handleSign}
              onCancel={() => setShowSignCanvas(false)}
            />
          </div>
        )}

        {/* Signature confirmed */}
        {quote.signature_data && (
          <div className="p-4 rounded-xl mb-4" style={{ background: 'rgba(61,220,151,0.08)', border: '1px solid rgba(61,220,151,0.2)' }}>
            <div className="flex items-center gap-3">
              <Check size={18} style={{ color: '#3DDC97', flexShrink: 0 }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: '#3DDC97' }}>Contrat signé légalement</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Par : {quote.signature_nom}
                  {quote.signature_date && ` — ${formatDateTime(quote.signature_date)}`}
                  {quote.signature_ip && ` — IP : ${quote.signature_ip}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment acompte */}
        {canPayAcompte && (
          <div className="p-5 rounded-xl mb-4" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.25)' }}>
            <h2 className="font-semibold text-sm mb-1" style={{ color: 'var(--teal)' }}>
              <Shield size={16} className="inline mr-1" /> Paiement de l'acompte (30%)
            </h2>
            <p className="text-xs mb-4" style={{ color: 'rgba(26,26,46,0.5)' }}>
              ⏰ Encaissement obligatoire <strong>1 semaine avant la livraison</strong>
            </p>

            {/* ── BLOC WALLET PARRAINAGE ── */}
            {walletBalance > 0 && (
              <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.25)' }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Wallet size={15} style={{ color: '#1F9D7A' }} />
                    <p className="text-sm font-bold" style={{ color: '#1F9D7A' }}>
                      💰 Crédit disponible : {walletBalance.toFixed(2)}€
                    </p>
                  </div>
                  <button
                    onClick={() => setWalletApplied(!walletApplied)}
                    className="text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
                    style={{
                      background: walletApplied ? '#1F9D7A' : 'rgba(31,157,122,0.1)',
                      color: walletApplied ? '#FFFFFF' : '#1F9D7A',
                      border: '1px solid rgba(31,157,122,0.3)',
                    }}>
                    {walletApplied ? '✓ Appliqué' : 'Utiliser mon crédit'}
                  </button>
                </div>
                {walletApplied && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span style={{ color: 'var(--text-muted)' }}>Acompte initial</span>
                      <span style={{ color: 'var(--text-muted)', textDecoration: 'line-through' }}>{quote.acompte?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span style={{ color: '#1F9D7A' }}>
                        <Tag size={11} className="inline mr-1" />Crédit utilisé
                      </span>
                      <span style={{ color: '#1F9D7A', fontWeight: 700 }}>-{Math.min(walletBalance, quote.acompte || 0).toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold pt-1" style={{ borderTop: '1px solid rgba(31,157,122,0.2)' }}>
                      <span style={{ color: '#0B4B4E' }}>À payer maintenant</span>
                      <span className="price-gold">{Math.max(0, (quote.acompte || 0) - Math.min(walletBalance, quote.acompte || 0)).toFixed(2)}€</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>À régler pour confirmer</p>
                <p className="text-xs" style={{ color: 'rgba(26,26,46,0.4)' }}>Solde de {quote.solde?.toFixed(2)}€ dû à la livraison</p>
              </div>
              <span className="price-gold text-2xl font-bold">
                {walletApplied
                  ? Math.max(0, (quote.acompte || 0) - Math.min(walletBalance, quote.acompte || 0)).toFixed(2)
                  : quote.acompte?.toFixed(2)}€
              </span>
            </div>

            {/* ── CHOIX MÉTHODE DE PAIEMENT ── */}
            <div className="mb-4">
              <p className="text-xs font-semibold mb-2" style={{ color: 'rgba(26,26,46,0.5)' }}>CHOISISSEZ VOTRE MODE DE PAIEMENT</p>

              {/* Alerte urgence < 7 jours */}
              {urgentLessDe7Jours && (
                <div className="flex items-start gap-2 p-3 rounded-xl mb-3"
                  style={{ background: 'rgba(229,62,62,0.07)', border: '1.5px solid rgba(229,62,62,0.25)' }}>
                  <span className="text-base flex-shrink-0">⚠️</span>
                  <div>
                    <p className="text-xs font-bold" style={{ color: '#D94B5D' }}>
                      Événement dans moins de 7 jours
                    </p>
                    <p className="text-xs" style={{ color: 'rgba(217,75,93,0.8)' }}>
                      Le délai d'encaissement étant dépassé, seul le paiement intégral par carte bancaire est accepté.
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-2">

                {/* Carte bancaire / Stripe — toujours disponible */}
                <button
                  data-testid="quote-deposit-pay-button"
                  onClick={() => initiatePayment(urgentLessDe7Jours ? 'total' : 'acompte')}
                  disabled={paymentLoading}
                  className="w-full btn-gold py-3.5 rounded-xl text-sm font-bold flex items-center gap-3 justify-between px-4 disabled:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💳</span>
                    <div className="text-left">
                      <p className="font-bold">Carte bancaire</p>
                      <p className="text-xs font-normal opacity-75">
                        {urgentLessDe7Jours ? 'Paiement total obligatoire — événement imminent' : 'Paiement en ligne sécurisé — immédiat'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span>
                      {urgentLessDe7Jours
                        ? `${quote.total?.toFixed(2)}€`
                        : `${walletApplied ? Math.max(0, (quote.acompte || 0) - Math.min(walletBalance, quote.acompte || 0)).toFixed(2) : quote.acompte?.toFixed(2)}€`}
                    </span>
                    <ArrowRight size={16} />
                  </div>
                </button>

                {/* Chèque et Espèces — seulement si > 7 jours */}
                {!urgentLessDe7Jours && (<>

                  {/* Chèque */}
                  <div className="w-full py-3.5 rounded-xl text-sm flex items-center gap-3 justify-between px-4"
                    style={{ background: 'rgba(93,188,191,0.06)', border: '1.5px solid rgba(93,188,191,0.25)' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📝</span>
                      <div>
                        <p className="font-bold" style={{ color: 'var(--ink)' }}>Chèque</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>À l'ordre de <strong>{company.raison_sociale}</strong></p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold price-gold">{quote.acompte?.toFixed(2)}€</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Envoi par courrier</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 rounded-lg text-xs" style={{ background: 'rgba(93,188,191,0.04)', color: 'rgba(26,26,46,0.5)' }}>
                    📬 Envoyez à : <strong>{company.adresse_encaissement}</strong>
                    <br/>⏰ À réceptionner <strong>au moins 7 jours avant la livraison</strong>
                  </div>

                  {/* Espèces */}
                  <div className="w-full py-3.5 rounded-xl text-sm flex items-center gap-3 justify-between px-4"
                    style={{ background: 'rgba(31,157,122,0.05)', border: '1.5px solid rgba(31,157,122,0.2)' }}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💵</span>
                      <div>
                        <p className="font-bold" style={{ color: 'var(--ink)' }}>Espèces</p>
                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Remise en main propre sur RDV</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold price-gold">{quote.acompte?.toFixed(2)}€</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Sur RDV</p>
                    </div>
                  </div>
                  <div className="px-4 py-2 rounded-lg text-xs" style={{ background: 'rgba(31,157,122,0.04)', color: 'rgba(26,26,46,0.5)' }}>
                    📞 Contactez-nous pour fixer le RDV
                    <br/>⏰ <strong>7 jours minimum avant la livraison</strong> · Limite légale 1 000€
                  </div>

                </>)}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2">
              <Shield size={13} style={{ color: 'rgba(26,26,46,0.3)' }} />
              <p className="text-xs" style={{ color: 'rgba(26,26,46,0.3)' }}>Paiement 100% sécurisé · Reçu envoyé par email</p>
            </div>

            {/* ── PAIEMENT À LA LIVRAISON — PRO & ASSOCIATIONS ── */}
            {isProOrAsso && !urgentLessDe7Jours && (
              <div className="mt-4 p-4 rounded-xl" style={{ background: 'rgba(125,177,255,0.06)', border: '1.5px solid rgba(125,177,255,0.25)' }}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">🏢</span>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#7DB1FF' }}>
                      Option exclusive {(PROFILE_DISCOUNTS[quote.client_profile]?.label) || 'Pro'} — Paiement à la livraison
                    </p>
                    <p className="text-xs" style={{ color: 'rgba(125,177,255,0.7)' }}>
                      Disponible uniquement pour les comptes entreprise, CE et associations
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* CB à la livraison */}
                  <div className="p-3 rounded-xl" style={{ background: 'rgba(125,177,255,0.08)', border: '1px solid rgba(125,177,255,0.2)' }}>
                    <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--ink)' }}>💳 CB à la livraison</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Terminal de paiement sur place</p>
                    <p className="text-sm font-bold price-gold mt-1">{quote.total?.toFixed(2)}€</p>
                  </div>
                  {/* Chèque à la livraison */}
                  <div className="p-3 rounded-xl" style={{ background: 'rgba(125,177,255,0.08)', border: '1px solid rgba(125,177,255,0.2)' }}>
                    <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--ink)' }}>📝 Chèque à la livraison</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Remis au livreur</p>
                    <p className="text-sm font-bold price-gold mt-1">{quote.total?.toFixed(2)}€</p>
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: 'rgba(26,26,46,0.45)' }}>
                  📞 Pour choisir cette option, contactez-nous avant la livraison afin de valider le mode de règlement. Une facture vous sera adressée par email après paiement.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Payment solde */}
        {canPaySolde && (
          <div className="p-5 rounded-xl mb-4" style={{ background: 'rgba(61,220,151,0.05)', border: '1px solid rgba(61,220,151,0.2)' }}>
            <div className="flex items-center gap-2 mb-3">
              <Check size={16} style={{ color: '#3DDC97' }} />
              <p className="text-sm font-semibold" style={{ color: '#3DDC97' }}>Acompte payé ✓</p>
            </div>
            <h2 className="font-semibold text-sm mb-3" style={{ color: '#3DDC97' }}>
              Paiement du solde (70%)
            </h2>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Solde restant à régler</p>
              <span className="text-xl font-bold" style={{ color: '#3DDC97' }}>{quote.solde?.toFixed(2)}€</span>
            </div>
            <button
              onClick={() => initiatePayment('solde')}
              disabled={soldeLoading}
              data-testid="quote-solde-pay-button"
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center gap-2 justify-center disabled:opacity-50"
              style={{ background: 'rgba(61,220,151,0.15)', color: '#3DDC97', border: '1px solid rgba(61,220,151,0.3)' }}
            >
              {soldeLoading ? 'Redirection...' : `Payer le solde — ${quote.solde?.toFixed(2)}€`}
              <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Fully paid */}
        {quote.statut === 'solde_paye' && (
          <div className="p-5 rounded-xl mb-4 text-center" style={{ background: 'rgba(61,220,151,0.08)', border: '1px solid rgba(61,220,151,0.25)' }}>
            <Star size={40} className="mx-auto mb-3" style={{ color: '#3DDC97' }} />
            <h3 className="font-semibold mb-1" style={{ color: '#3DDC97' }}>Prestation entièrement payée !</h3>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Merci pour votre confiance. Notre équipe vous contactera pour les détails logistiques.
            </p>
          </div>
        )}

        {/* Acompte paid confirmation */}
        {quote.statut === 'acompte_paye' && (
          <div className="p-4 rounded-xl mb-4 flex items-center gap-3" style={{ background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <Check size={18} style={{ color: 'var(--teal)', flexShrink: 0 }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--teal)' }}>Acompte encaissé — Réservation confirmée !</p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Solde restant : {quote.solde?.toFixed(2)}€ — dû à la livraison</p>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
